import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import NotificationToast from '../../../components/ui/NotificationToast';
import apiClient from '../../../lib/apiClient';

const HolidayManagement = ({ isAdmin, clientId }) => {
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [notification, setNotification] = useState(null);
  const [filters, setFilters] = useState({
    year: new Date().getFullYear(),
    is_national: ''
  });
  const [formData, setFormData] = useState({
    holiday_date: '',
    holiday_name: '',
    description: '',
    is_national: true
  });

  useEffect(() => {
    loadHolidays();
  }, [filters, clientId]);

  const loadHolidays = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.year) params.append('year', filters.year);
      if (filters.is_national) params.append('is_national', filters.is_national);
      if (clientId) params.append('client_id', clientId);

      const result = await apiClient.get(`/holidays?${params.toString()}`);
      if (result?.success) {
        setHolidays(result.data || []);
      }
    } catch (error) {
      setNotification({ message: 'Failed to load holidays', type: 'error' });
      console.error('Error loading holidays:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.holiday_date || !formData.holiday_name) {
      setNotification({ message: 'Please fill all required fields', type: 'error' });
      return;
    }

    setLoading(true);
    try {
      let result;
      if (editingId) {
        result = await apiClient.put(`/holidays/${editingId}`, formData);
      } else {
        result = await apiClient.post('/holidays', formData);
      }

      if (result?.success) {
        setNotification({ 
          message: editingId ? 'Holiday updated successfully' : 'Holiday added successfully', 
          type: 'success' 
        });
        setShowForm(false);
        setEditingId(null);
        resetForm();
        loadHolidays();
      } else {
        setNotification({ message: result?.error || 'Failed to save holiday', type: 'error' });
      }
    } catch (error) {
      setNotification({ message: 'An error occurred while saving holiday', type: 'error' });
      console.error('Error saving holiday:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this holiday?')) return;

    setLoading(true);
    try {
      const result = await apiClient.delete(`/holidays/${id}`);
      if (result?.success) {
        setNotification({ message: 'Holiday deleted successfully', type: 'success' });
        loadHolidays();
      } else {
        setNotification({ message: result?.error || 'Failed to delete holiday', type: 'error' });
      }
    } catch (error) {
      setNotification({ message: 'An error occurred while deleting holiday', type: 'error' });
      console.error('Error deleting holiday:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (holiday) => {
    setFormData({
      holiday_date: holiday.holiday_date,
      holiday_name: holiday.holiday_name,
      description: holiday.description || '',
      is_national: holiday.is_national !== undefined ? holiday.is_national : true
    });
    setEditingId(holiday.id);
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      holiday_date: '',
      holiday_name: '',
      description: '',
      is_national: true
    });
    setEditingId(null);
  };

  const yearOptions = Array.from({ length: 5 }, (_, i) => {
    const year = new Date().getFullYear() - 2 + i;
    return { value: year, label: String(year) };
  });

  return (
    <div className="p-6">
      <NotificationToast
        message={notification?.message}
        type={notification?.type}
        onDismiss={() => setNotification(null)}
      />

      <div className="flex items-center justify-between mb-6">
        <div>
          <h4 className="text-lg font-semibold text-foreground">Public Holidays</h4>
          <p className="text-sm text-muted-foreground">Manage public holiday calendar</p>
        </div>
        {isAdmin && (
          <Button onClick={() => { resetForm(); setShowForm(true); }} className="flex items-center gap-2">
            <Icon name="Plus" size={16} />
            Add Holiday
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="mb-6 p-4 bg-muted/50 rounded-lg border border-border">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Year</label>
            <select
              value={filters.year}
              onChange={(e) => setFilters(prev => ({ ...prev, year: Number(e.target.value) }))}
              className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {yearOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Type</label>
            <select
              value={filters.is_national}
              onChange={(e) => setFilters(prev => ({ ...prev, is_national: e.target.value }))}
              className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="">All Holidays</option>
              <option value="true">National Holidays</option>
              <option value="false">Regional Holidays</option>
            </select>
          </div>
        </div>
      </div>

      {/* Form Modal */}
      {showForm && isAdmin && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-card rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-foreground">
                  {editingId ? 'Edit Holiday' : 'Add New Holiday'}
                </h3>
                <button
                  onClick={() => { setShowForm(false); resetForm(); }}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <Icon name="X" size={20} />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Holiday Date *</label>
                <Input
                  type="date"
                  value={formData.holiday_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, holiday_date: e.target.value }))}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Holiday Name *</label>
                <Input
                  type="text"
                  value={formData.holiday_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, holiday_name: e.target.value }))}
                  placeholder="e.g., Republic Day, Diwali"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="Enter holiday description"
                />
              </div>
              <div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.is_national}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_national: e.target.checked }))}
                    className="rounded border-border"
                  />
                  <span className="text-sm text-foreground">Is National Holiday</span>
                </label>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => { setShowForm(false); resetForm(); }}>
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={loading}>
                  {loading ? 'Saving...' : 'Save Holiday'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Holidays Table */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading holidays...</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted/40">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Holiday Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Description</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Type</th>
                {isAdmin && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Actions</th>
                )}
              </tr>
            </thead>
            <tbody className="bg-card divide-y divide-border">
              {holidays.length === 0 ? (
                <tr>
                  <td colSpan={isAdmin ? 5 : 4} className="px-4 py-8 text-center text-muted-foreground">
                    No holidays found for {filters.year}
                  </td>
                </tr>
              ) : (
                holidays.map(holiday => (
                  <tr key={holiday.id} className="hover:bg-muted/50">
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-foreground">
                      {new Date(holiday.holiday_date).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-foreground">
                      {holiday.holiday_name}
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground">
                      {holiday.description || '-'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                        {holiday.is_national ? 'National' : 'Regional'}
                      </span>
                    </td>
                    {isAdmin && (
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleEdit(holiday)}
                            className="text-primary hover:text-primary/80"
                            title="Edit"
                          >
                            <Icon name="Edit" size={16} />
                          </button>
                          <button
                            onClick={() => handleDelete(holiday.id)}
                            className="text-destructive hover:text-destructive/80"
                            title="Delete"
                          >
                            <Icon name="Trash2" size={16} />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default HolidayManagement;

